import Add from '@mui/icons-material/Add'
import Close from '@mui/icons-material/Close'
import Videocam from '@mui/icons-material/Videocam'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { alpha, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'

import T from 'T'

const ACCEPTED_MEDIA = {
  'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.gif'],
  'video/*': ['.mp4', '.mov', '.webm'],
}

const THUMB_SIZE = 64

type MediaThumbProps = {
  file: File
  index: number
  disabled: boolean
  onRemove: (index: number) => void
}

const MediaThumb: React.FC<MediaThumbProps> = ({ file, index, disabled, onRemove }) => {
  const theme = useTheme()
  const handleRemove = useCallback(() => onRemove(index), [index, onRemove])
  const isVideo = file.type.startsWith('video/')

  // Object URLs must be created/revoked in an effect, not during render: render-phase creation
  // leaks on discarded renders and breaks under StrictMode's double-invoked cleanup (revoked
  // URLs kept by a memoized array render blank thumbnails). Each thumb owns its URL lifecycle.
  const [url, setUrl] = useState('')
  useEffect(() => {
    if (isVideo) return undefined
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file, isVideo])

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        height: THUMB_SIZE,
        overflow: 'hidden',
        position: 'relative',
        width: THUMB_SIZE,
      }}
    >
      {isVideo ? (
        <Box sx={{ alignItems: 'center', bgcolor: 'grey.800', display: 'flex', height: '100%', justifyContent: 'center', width: '100%' }}>
          <Videocam sx={{ color: 'common.white', fontSize: 24 }} />
        </Box>
      ) : (
        url && <Box alt="" component="img" src={url} sx={{ height: '100%', objectFit: 'cover', width: '100%' }} />
      )}
      {!disabled && (
        <IconButton
          aria-label={T.REMOVE}
          size="small"
          sx={{
            '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.7) },
            bgcolor: alpha(theme.palette.common.black, 0.55),
            color: 'common.white',
            p: 0.25,
            position: 'absolute',
            right: 2,
            top: 2,
          }}
          onClick={handleRemove}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  )
}

type ExceptionMediaUploaderProps = {
  files: File[]
  onChange: (files: File[]) => void
  disabled?: boolean
}

// Photo/video picker: a dashed drop area when empty, then thumbnails + a "+" tile. Files stage
// here and upload on save (client-direct to the uploads bucket, then registered via /{id}/media).
const buildStagedFileKey = (file: File): string => `${file.name}-${file.lastModified}-${file.size}`

const ExceptionMediaUploader: React.FC<ExceptionMediaUploaderProps> = ({ files, onChange, disabled = false }) => {
  // Re-adding an already-staged file must be a no-op: duplicates collapse visually under the same
  // thumb key but each entry uploads, silently inflating the persisted photo count.
  const handleDrop = useCallback(
    (accepted: File[]) => {
      const stagedKeys = new Set(files.map(buildStagedFileKey))
      const freshFiles = accepted.filter(file => !stagedKeys.has(buildStagedFileKey(file)))
      if (freshFiles.length > 0) onChange([...files, ...freshFiles])
    },
    [files, onChange]
  )

  const { getRootProps, getInputProps, open } = useDropzone({ accept: ACCEPTED_MEDIA, disabled, noClick: true, onDrop: handleDrop })

  const handleRemove = useCallback((index: number) => onChange(files.filter((_, fileIndex) => fileIndex !== index)), [files, onChange])

  const hasFiles = files.length > 0

  return (
    <Box {...getRootProps()} sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <input {...getInputProps()} />
      {files.map((file, index) => (
        <MediaThumb key={buildStagedFileKey(file)} disabled={disabled} file={file} index={index} onRemove={handleRemove} />
      ))}
      {hasFiles ? (
        <Box
          sx={{
            alignItems: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.secondary',
            cursor: disabled ? 'default' : 'pointer',
            display: 'flex',
            height: THUMB_SIZE,
            justifyContent: 'center',
            width: THUMB_SIZE,
          }}
          onClick={disabled ? undefined : open}
        >
          <Add />
        </Box>
      ) : (
        <Box
          sx={{
            alignItems: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.secondary',
            cursor: disabled ? 'default' : 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            height: THUMB_SIZE,
            justifyContent: 'center',
            width: '100%',
          }}
          onClick={disabled ? undefined : open}
        >
          <Add />
          <Typography color="textSecondary" variant="body2">
            {T.ADD_PHOTOS_OR_VIDEOS}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default ExceptionMediaUploader
